import {InvalidTokenError} from '@packages/utils';
import {parseCookies} from 'nookies';
import {useEffect, useState} from 'react';
import {deleteLike, postLike} from '../../../core/repositories/boxRepository';
import {validateCookie} from '../../../core/usecases/auth/checkOauthTokenExistence';
import {useIsSessionExpired} from '../../../core/usecases/auth/useIsSessionExpired';
import {useBoxLocalStorage} from './useBoxLocalStorage';

type UseAnswrLikedOptions = {
  questionId: number;
  answerId: number;
  adviserName: string;
  isLikedByAPI: boolean;
  likedCountByAPI: number;
};

export function useAnswerLiked({
  questionId,
  answerId,
  adviserName,
  isLikedByAPI,
  likedCountByAPI,
}: UseAnswrLikedOptions) {
  const [isLiked, setIsLiked] = useState<boolean>(isLikedByAPI);
  const [likedCount, setLikedCount] = useState<number>(likedCountByAPI);
  const {storedValue, updateStoredValue} = useBoxLocalStorage();
  const cookies = parseCookies();
  const {isValid} = validateCookie(cookies);

  useEffect(() => {
    if (!isValid && storedValue.likedAnswerIds) {
      setIsLiked(storedValue.likedAnswerIds.includes(answerId));
    }
  }, [isValid, answerId, storedValue]);

  const {updateIsSessionExpired} = useIsSessionExpired();

  const handleLiked = async () => {
    if (isLiked) {
      if (!isValid && storedValue.likedAnswerIds) {
        updateStoredValue({
          likedAnswerIds: storedValue.likedAnswerIds.filter(
            (id: number) => id !== answerId,
          ),
        });
      }

      const result = await deleteLike({
        cookies,
        questionId,
        adviserName,
      });
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }

      setIsLiked(false);
      setLikedCount(likedCount - 1);

      return;
    }

    if (!isValid) {
      updateStoredValue({
        likedAnswerIds: storedValue.likedAnswerIds
          ? [...storedValue.likedAnswerIds, answerId]
          : [answerId],
      });
    }

    const result = await postLike({
      cookies,
      questionId,
      adviserName,
    });
    if (!result.ok && result.error === InvalidTokenError) {
      updateIsSessionExpired(true);
    }

    setIsLiked(true);
    setLikedCount(likedCount + 1);
  };

  return {
    isLiked,
    likedCount,
    handleLiked,
  };
}
